import React, { useState, useMemo, useCallback } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

// ─── helpers ──────────────────────────────────────────────────────────────────
const inr = (n) =>
  "₹ " + (parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getTodayRange = () => {
  const t = new Date().toISOString().split("T")[0];
  return { start: t, end: t };
};

const buildUrl = (userid, start, end, memberId = "") => {
  let url = `https://pro.mokshiq.in/fund_summary_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (memberId) url += `&member_id=${memberId}`;
  return url;
};

const FLOW_ICONS = {
  credit: "ph-arrow-circle-down",
  debit:  "ph-arrow-circle-up",
};

// ─── Modern Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, iconBg, iconColor }) {
  return (
    <div
      className="card border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden h-100"
      style={{ padding: "10px 14px" }}
    >
      <div className="d-flex align-items-center position-relative z-1" style={{ gap: "14px" }}>
        <div
          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: "36px",
            height: "36px",
            backgroundColor: iconBg,
            color: iconColor
          }}
        >
          <i className={`ph-duotone ${icon}`} style={{ fontSize: "18px" }}></i>
        </div>
        <div className="overflow-hidden min-w-0 flex-grow-1">
          <span
            className="text-muted text-uppercase fw-semibold d-block text-truncate"
            style={{ fontSize: "10.5px", letterSpacing: "0.03em", marginBottom: "1px" }}
            title={label}
          >
            {label}
          </span>
          <h5
            className="fw-extrabold text-dark mb-0 text-truncate"
            style={{ fontSize: "15px", lineHeight: "1.2" }}
          >
            {value}
          </h5>
        </div>
      </div>

      <div
        className="position-absolute end-0 bottom-0 pe-none"
        style={{
          color: iconColor,
          opacity: 0.1,
          right: "6px",
          bottom: "4px"
        }}
      >
        <i className="ph-duotone ph-chart-bar" style={{ fontSize: "32px" }}></i>
      </div>
    </div>
  );
}

// ─── expandable day-wise member row ──────────────────────────────────────────
function DayRow({ row, isOpen, onToggle }) {
  const net = (row.total_credit || 0) - (row.total_debit || 0);
  return (
    <>
      <tr
        className="cursor-pointer"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <td>
          <span
            className="badge rounded-pill fw-bold px-2.5 py-1"
            style={{ backgroundColor: "#f3e8ff", color: "#7e57c2", fontSize: "11.5px" }}
          >
            {row.txn_date}
          </span>
        </td>
        <td>
          <div className="fw-bold text-dark" style={{ fontSize: "13.5px" }}>{row.member_name || "—"}</div>
          <div className="text-muted" style={{ fontSize: "11.5px" }}>{row.member_phone}</div>
        </td>
        <td>
          <span className="badge bg-light text-secondary rounded-pill fw-medium" style={{ fontSize: "11px" }}>
            {row.member_role}
          </span>
        </td>
        <td className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{inr(row.opening_balance)}</td>
        <td>
          <span className="text-success fw-bold" style={{ fontSize: "13px" }}>
            <i className="ph-bold ph-arrow-circle-down me-1"></i>
            {inr(row.total_credit)}
          </span>
          <div className="text-muted" style={{ fontSize: "11px" }}>{row.credit_count} txn{row.credit_count !== 1 ? "s" : ""}</div>
        </td>
        <td>
          <span className="text-danger fw-bold" style={{ fontSize: "13px" }}>
            <i className="ph-bold ph-arrow-circle-up me-1"></i>
            {inr(row.total_debit)}
          </span>
          <div className="text-muted" style={{ fontSize: "11px" }}>{row.debit_count} txn{row.debit_count !== 1 ? "s" : ""}</div>
        </td>
        <td>
          <span className={`fw-bold style={{ fontSize: "13px" }} ${net >= 0 ? "text-success" : "text-danger"}`}>
            {net >= 0 ? "+" : ""}{inr(net)}
          </span>
        </td>
        <td className="fw-bold text-primary" style={{ fontSize: "13px" }}>{inr(row.closing_balance)}</td>
        <td>
          <i className={`ph-bold ${isOpen ? "ph-caret-up" : "ph-caret-down"} text-secondary`}></i>
        </td>
      </tr>

      {/* Expanded transaction details */}
      {isOpen && (
        <tr>
          <td colSpan={9} style={{ padding: 0, background: "#f8fafc" }}>
            <div className="p-3">
              <div className="row g-2">
                {(row.transactions || []).map((t, i) => (
                  <div className="col-md-6 col-xl-4" key={i}>
                    <div
                      className="card border-0 shadow-xs mb-0 rounded-3 bg-white"
                      style={{ borderLeft: `4px solid ${t.flow === "credit" ? "#16a34a" : "#dc2626"}` }}
                    >
                      <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span
                                className="badge rounded-pill fw-bold px-2 py-0.5"
                                style={{
                                  backgroundColor: t.flow === "credit" ? "#dcfce7" : "#ffe4e6",
                                  color: t.flow === "credit" ? "#15803d" : "#be123c",
                                  fontSize: "10.5px"
                                }}
                              >
                                <i className={`ph-fill ${FLOW_ICONS[t.flow]} me-1`}></i>
                                {t.flow?.toUpperCase()}
                              </span>
                              <span
                                className="fw-bold"
                                style={{
                                  fontSize: "14px",
                                  color: t.flow === "credit" ? "#16a34a" : "#dc2626"
                                }}
                              >
                                {inr(t.amount)}
                              </span>
                            </div>
                            <div className="text-secondary mb-1" style={{ fontSize: "11.5px" }}>{t.flow_label}</div>
                            {t.utr && (
                              <div className="d-flex align-items-center gap-1">
                                <i className="ph ph-hash text-secondary" style={{ fontSize: "11px" }}></i>
                                <span className="fw-medium text-primary" style={{ fontSize: "11.5px" }}>{t.utr}</span>
                              </div>
                            )}
                            {t.remark && (
                              <div className="text-secondary mt-1" style={{ fontSize: "11px" }}>
                                <i className="ph ph-note me-1"></i>{t.remark}
                              </div>
                            )}
                          </div>
                          <div className="text-end ms-2" style={{ minWidth: 90 }}>
                            <div className="text-muted" style={{ fontSize: "11px" }}>{t.txn_datetime}</div>
                            <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                              <span>Open: </span>{inr(t.opening_balance)}
                            </div>
                            <div className="fw-semibold text-primary mt-0" style={{ fontSize: "11px" }}>
                              <span>Close: </span>{inr(t.closing_balance)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
function FundSummaryReportPage() {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  const { start, end } = getTodayRange();

  const [fromDate, setFromDate]   = useState(start);
  const [toDate, setToDate]       = useState(end);
  const [memberId, setMemberId]   = useState("");
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [activeTab, setActiveTab] = useState("summary"); // "summary" | "detail" | "members"
  const [page, setPage]           = useState(1);
  const PAGE_SIZE = 50;

  const fetchData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const url = buildUrl(userid, fromDate, toDate, memberId);
      const res = await fetchWrapper(url);
      if (res.status) {
        setData(res);
        setPage(pg);
      } else {
        showMainToast(res.error || "Failed to load data", "danger");
      }
    } catch (e) {
      showMainToast("Network error", "danger");
    } finally {
      setLoading(false);
    }
  }, [userid, fromDate, toDate, memberId]);

  const g = data?.grand_totals || {};
  const summary = data?.day_wise_summary || [];
  const members = data?.member_totals   || [];
  const detail  = data?.data            || [];
  const pagination = data?.pagination   || {};

  // Paginate detail client-side for the table
  const detailPage = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return detail.slice(start, start + PAGE_SIZE);
  }, [detail, page]);

  const exportCSV = () => {
    if (!detail.length) { showMainToast("No data to export", "warning"); return; }
    const keys = Object.keys(detail[0]);
    let csv = keys.join(",") + "\n";
    detail.forEach(r => {
      csv += keys.map(k => {
        const v = String(r[k] ?? "").replace(/"/g, '""');
        return v.includes(",") ? `"${v}"` : v;
      }).join(",") + "\n";
    });
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `fund_summary_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <main style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <div className="container-fluid p-3 p-md-4">

        {/* 1. Branded Hero Header Banner Card */}
        <div
          className="card border-0 mb-3 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "14px"
          }}
        >
          <div className="card-body py-3 px-3.5 position-relative z-1">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    backgroundColor: "#7e57c2",
                    color: "#ffffff"
                  }}
                >
                  <i
                    className="ph-bold ph-chart-line-up"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div>
                  <h5
                    className="fw-extrabold mb-0.5"
                    style={{ color: "#0f172a", fontSize: "17px" }}
                  >
                    Fund Summary Report
                  </h5>
                  <p
                    className="text-muted mb-1"
                    style={{ fontSize: "12px" }}
                  >
                    Track opening balance, credit, debit, and closing balance per member per day.
                  </p>
                  <div className="d-flex align-items-center gap-1.5 text-secondary" style={{ fontSize: "11.5px" }}>
                    <i className="ph-bold ph-house-line" style={{ color: "#7e57c2" }}></i>
                    <span>Reports</span>
                    <span>/</span>
                    <span className="fw-semibold" style={{ color: "#7e57c2" }}>
                      Fund Summary
                    </span>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  className="btn bg-white border border-light-subtle rounded-3 shadow-xs d-inline-flex align-items-center gap-1.5 px-3 py-1 fw-bold text-nowrap"
                  style={{
                    fontSize: "11.5px",
                    color: "#673ab7",
                    backgroundColor: "#ffffff",
                    height: "32px",
                    whiteSpace: "nowrap"
                  }}
                  onClick={exportCSV}
                >
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ fontSize: "13px", color: "#673ab7" }}
                  ></i>
                  Export CSV
                </button>

                <img
                  src="/assets/images/fund_summary_report_3d.png"
                  alt="Fund Summary 3D Illustration"
                  className="d-none d-sm-block"
                  style={{
                    maxHeight: "70px",
                    objectFit: "contain"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2. Standalone Filter Card */}
        <div className="card border-0 mb-3 rounded-4 shadow-sm bg-white p-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                backgroundColor: "#7e57c2",
                color: "#ffffff"
              }}
            >
              <i
                className="ph-bold ph-funnel"
                style={{ fontSize: "20px" }}
              ></i>
            </div>
            <div>
              <h5
                className="fw-bold text-dark mb-0.5"
                style={{ fontSize: "17px" }}
              >
                Filter
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                Select date range and member (optional) to generate fund summary report.
              </p>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchData(1);
            }}
          >
            <div className="row g-3 align-items-end">
              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  From Date
                </label>
                <div className="position-relative">
                  <input
                    type="date"
                    className="form-control border-light-subtle rounded-3 ps-3 pe-3"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  To Date
                </label>
                <div className="position-relative">
                  <input
                    type="date"
                    className="form-control border-light-subtle rounded-3 ps-3 pe-3"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    value={toDate}
                    min={fromDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <label
                  className="form-label text-secondary fw-semibold mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Member ID <span className="text-muted fw-normal">(optional)</span>
                </label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control border-light-subtle rounded-3 ps-3 pe-3"
                    style={{
                      fontSize: "13px",
                      backgroundColor: "#f8fafc",
                      color: "#1e293b",
                      height: "42px"
                    }}
                    placeholder="Filter single member"
                    value={memberId}
                    onChange={(e) => setMemberId(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <button
                  type="submit"
                  className="btn text-white fw-bold d-inline-flex align-items-center justify-content-center gap-2 w-100 shadow-xs"
                  style={{
                    background:
                      "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                    borderRadius: "12px",
                    height: "42px",
                    fontSize: "13.5px"
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Loading…
                    </>
                  ) : (
                    <>
                      <i
                        className="ph-bold ph-magnifying-glass"
                        style={{ fontSize: "16px" }}
                      ></i>
                      Fetch Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* 3. Grand Totals Cards */}
        {data && (
          <div className="row g-2.5 mb-3">
            <div className="col-6 col-md-3">
              <StatCard
                label="Total Credit"
                value={inr(g.total_credit)}
                icon="ph-arrow-circle-down"
                iconBg="#dcfce7"
                iconColor="#16a34a"
              />
            </div>
            <div className="col-6 col-md-3">
              <StatCard
                label="Total Debit"
                value={inr(g.total_debit)}
                icon="ph-arrow-circle-up"
                iconBg="#ffe4e6"
                iconColor="#f43f5e"
              />
            </div>
            <div className="col-6 col-md-3">
              <StatCard
                label="Total Transactions"
                value={g.total_txns?.toLocaleString("en-IN") || 0}
                icon="ph-receipt"
                iconBg="#e0e7ff"
                iconColor="#4f46e5"
              />
            </div>
            <div className="col-6 col-md-3">
              <StatCard
                label="Members"
                value={`${g.member_count || 0} · ${g.day_count || 0} days`}
                icon="ph-users-three"
                iconBg="#fef3c7"
                iconColor="#d97706"
              />
            </div>
          </div>
        )}

        {/* 4. Tabbed Content Container */}
        {data && (
          <div className="card border-0 rounded-4 shadow-sm overflow-hidden mb-4 bg-white">
            <div className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <ul className="nav nav-pills gap-2" role="tablist">
                {[
                  { key: "summary", label: "Day-wise Summary", icon: "ph-calendar-blank" },
                  { key: "members", label: "By Member",        icon: "ph-users-three"    },
                  { key: "detail",  label: "All Transactions", icon: "ph-list-dashes"    },
                ].map(({ key, label, icon }) => (
                  <li className="nav-item" key={key}>
                    <button
                      className={`btn btn-sm rounded-pill px-3 py-1.5 fw-bold d-inline-flex align-items-center gap-1.5 ${
                        activeTab === key
                          ? "btn-primary text-white shadow-xs"
                          : "btn-light text-secondary border-0"
                      }`}
                      style={{
                        backgroundColor: activeTab === key ? "#7e57c2" : "#f1f5f9",
                        borderColor: activeTab === key ? "#7e57c2" : "transparent",
                        fontSize: "12.5px"
                      }}
                      onClick={() => setActiveTab(key)}
                    >
                      <i className={`ph-bold ${icon}`}></i>
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
              <span className="badge bg-light text-secondary rounded-pill px-3 py-1.5 fw-medium" style={{ fontSize: "12px" }}>
                {data.role} · {data.filters?.start_date} — {data.filters?.end_date}
              </span>
            </div>

            <div className="card-body p-0">

              {/* Day-wise Summary Tab */}
              {activeTab === "summary" && (
                <div className="table-responsive">
                  {summary.length === 0 ? (
                    <div className="text-center py-5 text-secondary">
                      <i className="ph-duotone ph-calendar-x d-block mb-2" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                      <p className="mb-0" style={{ fontSize: "13px" }}>No fund transactions found for this period</p>
                    </div>
                  ) : (
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ background: "#f8fafc" }}>
                        <tr>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Date</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Member</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Role</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Opening Bal</th>
                          <th className="fw-semibold text-success py-3" style={{ fontSize: "12px" }}>Credit Received</th>
                          <th className="fw-semibold text-danger py-3" style={{ fontSize: "12px" }}>Debit Transferred</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Net</th>
                          <th className="fw-semibold text-primary py-3" style={{ fontSize: "12px" }}>Closing Bal</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.map((row) => {
                          const key = `${row.txn_date}-${row.member_id}`;
                          return (
                            <DayRow
                              key={key}
                              row={row}
                              isOpen={expandedKey === key}
                              onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                            />
                          );
                        })}
                      </tbody>
                      <tfoot style={{ background: "#f1f5f9", borderTop: "2px solid #e2e8f0" }}>
                        <tr>
                          <td colSpan={3} className="fw-bold text-primary py-3" style={{ fontSize: "13px" }}>TOTAL</td>
                          <td className="text-secondary" style={{ fontSize: "12px" }}>—</td>
                          <td className="fw-bold text-success py-3" style={{ fontSize: "13px" }}>{inr(g.total_credit)}</td>
                          <td className="fw-bold text-danger py-3" style={{ fontSize: "13px" }}>{inr(g.total_debit)}</td>
                          <td className={`fw-bold py-3 ${(g.total_credit - g.total_debit) >= 0 ? "text-success" : "text-danger"}`} style={{ fontSize: "13px" }}>
                            {inr(g.total_credit - g.total_debit)}
                          </td>
                          <td className="text-secondary" style={{ fontSize: "12px" }}>—</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}

              {/* By Member Tab */}
              {activeTab === "members" && (
                <div className="table-responsive">
                  {members.length === 0 ? (
                    <div className="text-center py-5 text-secondary">
                      <i className="ph-duotone ph-users d-block mb-2" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                      <p className="mb-0" style={{ fontSize: "13px" }}>No member data</p>
                    </div>
                  ) : (
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ background: "#f8fafc" }}>
                        <tr>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Member</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Role</th>
                          <th className="fw-semibold text-success py-3" style={{ fontSize: "12px" }}>Total Credit</th>
                          <th className="fw-semibold text-danger py-3" style={{ fontSize: "12px" }}>Total Debit</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Net</th>
                          <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Txns</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, i) => (
                          <tr key={i}>
                            <td>
                              <div className="fw-bold text-dark" style={{ fontSize: "13.5px" }}>{m.member_name || "—"}</div>
                              <div className="text-muted" style={{ fontSize: "11.5px" }}>{m.member_phone}</div>
                            </td>
                            <td>
                              <span className="badge bg-light text-secondary rounded-pill fw-medium" style={{ fontSize: "11px" }}>
                                {m.member_role}
                              </span>
                            </td>
                            <td className="text-success fw-bold" style={{ fontSize: "13px" }}>{inr(m.total_credit)}</td>
                            <td className="text-danger fw-bold" style={{ fontSize: "13px" }}>{inr(m.total_debit)}</td>
                            <td>
                              <span className={`fw-bold ${m.net >= 0 ? "text-success" : "text-danger"}`} style={{ fontSize: "13px" }}>
                                {m.net >= 0 ? "+" : ""}{inr(m.net)}
                              </span>
                            </td>
                            <td>
                              <span className="badge rounded-pill fw-bold px-2.5 py-1" style={{ backgroundColor: "#f3e8ff", color: "#7e57c2", fontSize: "11px" }}>
                                {m.total_txns}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ background: "#f1f5f9", borderTop: "2px solid #e2e8f0" }}>
                        <tr>
                          <td colSpan={2} className="fw-bold text-primary py-3" style={{ fontSize: "13px" }}>TOTAL ({members.length} members)</td>
                          <td className="fw-bold text-success py-3" style={{ fontSize: "13px" }}>{inr(g.total_credit)}</td>
                          <td className="fw-bold text-danger py-3" style={{ fontSize: "13px" }}>{inr(g.total_debit)}</td>
                          <td className={`fw-bold py-3 ${(g.total_credit - g.total_debit) >= 0 ? "text-success" : "text-danger"}`} style={{ fontSize: "13px" }}>
                            {inr(g.total_credit - g.total_debit)}
                          </td>
                          <td className="fw-bold text-primary py-3" style={{ fontSize: "13px" }}>{g.total_txns}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}

              {/* All Transactions Tab */}
              {activeTab === "detail" && (
                <>
                  <div className="table-responsive">
                    {detail.length === 0 ? (
                      <div className="text-center py-5 text-secondary">
                        <i className="ph-duotone ph-receipt d-block mb-2" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                        <p className="mb-0" style={{ fontSize: "13px" }}>No transactions found</p>
                      </div>
                    ) : (
                      <table className="table table-hover align-middle mb-0">
                        <thead style={{ background: "#f8fafc" }}>
                          <tr>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Date & Time</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Member</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Type</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Amount</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>UTR / Ref</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Opening</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Closing</th>
                            <th className="fw-semibold text-secondary py-3" style={{ fontSize: "12px" }}>Remark</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPage.map((t, i) => (
                            <tr key={i}>
                              <td>
                                <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{t.txn_date}</div>
                                <div className="text-muted" style={{ fontSize: "11px" }}>{t.txn_datetime?.split(" ").slice(1).join(" ")}</div>
                              </td>
                              <td>
                                <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>{t.member_name || "—"}</div>
                                <div className="text-muted" style={{ fontSize: "11px" }}>{t.member_phone}</div>
                              </td>
                              <td>
                                <span
                                  className="badge rounded-pill fw-bold px-2 py-0.5"
                                  style={{
                                    backgroundColor: t.flow === "credit" ? "#dcfce7" : "#ffe4e6",
                                    color: t.flow === "credit" ? "#15803d" : "#be123c",
                                    fontSize: "11px"
                                  }}
                                >
                                  <i className={`ph-fill ${FLOW_ICONS[t.flow]} me-1`}></i>
                                  {t.flow?.toUpperCase()}
                                </span>
                                <div className="text-muted mt-0.5" style={{ fontSize: "11px" }}>{t.flow_label}</div>
                              </td>
                              <td>
                                <span className={`fw-bold style={{ fontSize: "13.5px" }} ${t.flow === "credit" ? "text-success" : "text-danger"}`}>
                                  {t.flow === "credit" ? "+" : "-"}{inr(t.amount)}
                                </span>
                              </td>
                              <td>
                                {t.utr ? (
                                  <span className="fw-semibold text-primary" style={{ fontSize: "12px" }}>
                                    <i className="ph ph-hash me-1"></i>{t.utr}
                                  </span>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: "12px" }}>—</span>
                                )}
                                {t.ref_id && t.ref_id !== t.utr && (
                                  <div className="text-muted" style={{ fontSize: "11px" }}>{t.ref_id}</div>
                                )}
                              </td>
                              <td className="text-secondary" style={{ fontSize: "12.5px" }}>{inr(t.opening_balance)}</td>
                              <td className="fw-semibold text-primary" style={{ fontSize: "12.5px" }}>{inr(t.closing_balance)}</td>
                              <td className="text-secondary" style={{ fontSize: "12px", maxWidth: 140 }}>
                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {t.remark || "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {detail.length > PAGE_SIZE && (
                    <div className="d-flex align-items-center justify-content-between px-3 py-2.5 border-top bg-white">
                      <span className="text-muted" style={{ fontSize: "12.5px" }}>
                        Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, detail.length)} of {detail.length}
                      </span>
                      <div className="d-flex gap-1.5">
                        <button
                          className="btn btn-sm btn-light text-secondary rounded-2 px-2"
                          disabled={page === 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          <i className="ph-bold ph-caret-left"></i>
                        </button>
                        {Array.from({ length: Math.min(Math.ceil(detail.length / PAGE_SIZE), 7) }, (_, i) => {
                          const pg = Math.max(1, page - 3) + i;
                          if (pg > Math.ceil(detail.length / PAGE_SIZE)) return null;
                          return (
                            <button
                              key={pg}
                              className={`btn btn-sm rounded-2 px-2.5 fw-semibold ${
                                pg === page ? "btn-primary text-white" : "btn-light text-secondary"
                              }`}
                              style={{
                                backgroundColor: pg === page ? "#7e57c2" : "#f1f5f9",
                                borderColor: "transparent",
                                fontSize: "12px"
                              }}
                              onClick={() => setPage(pg)}
                            >{pg}</button>
                          );
                        })}
                        <button
                          className="btn btn-sm btn-light text-secondary rounded-2 px-2"
                          disabled={page * PAGE_SIZE >= detail.length}
                          onClick={() => setPage(p => p + 1)}
                        >
                          <i className="ph-bold ph-caret-right"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        )}

        {/* 5. Empty state before first fetch */}
        {!data && !loading && (
          <div className="card border-0 rounded-4 shadow-sm bg-white p-5 text-center my-4">
            <div className="card-body py-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: "72px",
                  height: "72px",
                  backgroundColor: "#f3e8ff",
                  color: "#7e57c2"
                }}
              >
                <i
                  className="ph-duotone ph-chart-line-up"
                  style={{ fontSize: "36px" }}
                ></i>
              </div>
              <h5 className="fw-bold text-dark mb-1" style={{ fontSize: "18px" }}>
                Select a date range and click Fetch Report
              </h5>
              <p
                className="text-muted mx-auto mb-4"
                style={{ fontSize: "13.5px", maxWidth: "560px" }}
              >
                Shows opening balance, total credit, total debit, and closing balance per member per day — covering all fund transfers and credit/debit transactions.
              </p>
              <div className="d-flex justify-content-center w-100">
                <button
                  type="button"
                  className="btn text-white fw-bold px-3.5 py-1.5 rounded-pill shadow-xs d-inline-flex align-items-center justify-content-center gap-2"
                  style={{
                    background:
                      "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                    fontSize: "12.5px",
                    height: "36px",
                    minWidth: "160px",
                    maxWidth: "200px"
                  }}
                  onClick={() => fetchData(1)}
                >
                  <i
                    className="ph-bold ph-paper-plane-tilt"
                    style={{ fontSize: "14px" }}
                  ></i>
                  Fetch Report Now
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

export default FundSummaryReportPage;